import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { load } from 'actions/admin';
import { Box, Loading, SystemText, DebugInfo } from '@salesperformanceint/e4shared';
import PageHeader from 'components/general/pageHeader';
import NoResults from 'components/general/noResults';
import { apiGet, apiPost, apiDelete, apiPatch } from 'services';
import SearchField from 'components/general/searchField';
import ProfileGroupModal from './components/profileGroupModal';
import GroupMembership from './components/membership';
import { setPrismMenu } from 'actions/ui';

export const AdminProfileGroups = () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = React.useState(true);
    const [searchFilter, setSearchFilter] = React.useState('');
    const [page, setPage] = React.useState(1);
    const [sortBy, setSortBy] = React.useState('created');
    const [descending, setDescending] = React.useState(true);
    const [recordCount, setRecordCount] = React.useState(0);
    const [data, setData] = React.useState(null);
    const [selected, setSelected] = React.useState([]);
    const [selectAll, setSelectAll] = React.useState(false);
    const [showProfilePicker, setShowProfilePicker] = React.useState(false);
    const [users, setUsers] = React.useState([]);
    const [profileGroup, setProfileGroup] = React.useState(null);
    const [collections, setCollections] = React.useState([]);
    const [status, setStatus] = React.useState(null);
    const user = useSelector(state => state.user.user);
    const [mode, setMode] = React.useState('add'); // add | edit

    const allProfiles = useSelector(state => state.admin.profiles) || [];
    const activeUsers = allProfiles.filter(p => p.active);

    React.useEffect(() => {
        dispatch(setPrismMenu('Admin'));
    }, []);

    React.useEffect(() => {
        const promises = [];

        promises.push(dispatch(load('profile')));
        promises.push(api.loadProfileGroups());
        promises.push(api.loadCollections());

        if (!promises.length) {
            setLoading(false);
        } else {
            Promise.all(promises).then(() => {
                setLoading(false);
            });
        }

        setUsers(activeUsers);
    }, []);

    const api = {
        loadProfileGroups: async onComplete => {
            const qs = `?page=${page}&status=${status}&sort=${sortBy}&desc=${descending}&search=${searchFilter}`;

            await apiGet(`profilegroups-pagingsorting${qs}`, user.guid, response => {
                setData(response.profileGroups);
                setRecordCount(response.recordCount);
            });

            onComplete?.();
        },
        loadCollections: async () => {
            await apiGet('collections', user.guid, response => {
                setCollections(response);
            });
        },
        getProfileGroupDetails: async (profileGroupId, onComplete) => {
            await apiGet(`profilegroups/${profileGroupId}`, user.guid, response => {
                const editProfileGroup = {
                    id: response.id,
                    name: response.name,
                    collectionIds: response.collectionsAssigned?.map(x => x.id) ?? [],
                    profileGroupProfiles: response.profileGroupProfiles,
                    canOverrideProfiles: response.canOverrideProfiles,
                    canBeDeleted: response.canBeDeleted,
                };

                setProfileGroup(editProfileGroup);
                onComplete?.();
            });
        },
        createProfileGroup: async createProfileGroupDto => {
            await apiPost('profilegroups', createProfileGroupDto, user.guid, () => {
                api.loadProfileGroups();
            });
        },
        updateProfileGroup: async updateProfileGroupDto => {
            await apiPatch(`profilegroups/${updateProfileGroupDto.ProfileGroupId}`, updateProfileGroupDto, user.guid, () => {
                api.loadProfileGroups();
            });
        },
        deleteProfileGroup: async profileGroupId => {
            apiDelete(`profilegroups/${profileGroupId}`, null, user.guid, () => {
                api.loadProfileGroups();
            });
        },
    };

    const sortProps = {
        sortBy,
        descending,
        setSortBy,
        setDescending,
    };

    React.useEffect(() => {
        const temp = selectAll ? data.map(d => d.id) : [];
        setSelected(temp);
    }, [selectAll]);

    React.useEffect(() => {
        setPage(1);
        setLoading(true);
        api.loadProfileGroups(() => setLoading(false));
    }, [searchFilter, status, sortBy, descending]);

    React.useEffect(() => {
        setLoading(true);
        api.loadProfileGroups(() => setLoading(false));
    }, [page]);

    React.useEffect(() => {
        DebugInfo('component', 'AdminProfileGroups');
    }, []);

    const extraButtons = () => (
        <button
            className="btn btn-success"
            onClick={() => {
                setMode('add');

                const emptyProfileGroup = {
                    id: null,
                    name: '',
                    collectionIds: [],
                    includeProfileIds: [],
                };

                setProfileGroup(emptyProfileGroup);
                setShowProfilePicker(true);
            }}
        >
            <i className="mr10 fa fa-users" />
            {SystemText('Admin/UserGroup/Buttons/AddNew')}
        </button>
    );

    const onRemove = async profileGroupId => {
        api.deleteProfileGroup(profileGroupId);
    };

    const onEdit = async profileGroupId => {
        setMode('edit');
        api.getProfileGroupDetails(profileGroupId, () => {
            setShowProfilePicker(true);
        });
    };

    const onSave = async profileGroup => {
        const { id, name, collectionIds, includeProfileIds } = profileGroup;

        if (mode === 'add') {
            const createProfileGroupDto = {
                Name: name,
                Id: id,
                CollectionIds: collectionIds,
                ProfileIdsToInclude: includeProfileIds,
            };

            await api.createProfileGroup(createProfileGroupDto);
        } else if (mode === 'edit') {
            const updateProfileGroupDto = {
                Name: name,
                ProfileGroupId: id,
                CollectionIds: collectionIds,
                ProfileIdsToInclude: includeProfileIds,
            };

            await api.updateProfileGroup(updateProfileGroupDto);
        }
    };

    return (
        <div>
            <PageHeader title={SystemText('Admin/UserGroup/Title')} extra={extraButtons} adminMenu />
            <section className="content mtm10 tracker" data-testid="review-tracker-content">
                <Box>
                    <div className="mb10">
                        <SearchField
                            value={searchFilter}
                            onChange={setSearchFilter}
                            minLengthForSearch={3}
                            placeholder={SystemText('Admin/UserGroup/Filter/Help')}
                        ></SearchField>
                    </div>

                    <div>
                        {loading ? (
                            <Loading />
                        ) : recordCount ? (
                            <GroupMembership
                                data={data}
                                page={page}
                                status={status}
                                setStatus={setStatus}
                                loading={loading}
                                setPage={setPage}
                                selected={selected}
                                setSelected={setSelected}
                                selectAll={selectAll}
                                setSelectAll={setSelectAll}
                                {...sortProps}
                                reload={() => {
                                    setSelectAll(false);
                                    setSelected([]);
                                    api.loadProfileGroups();
                                }}
                                onRemove={onRemove}
                                onEdit={onEdit}
                                user={user}
                                recordCount={recordCount}
                            />
                        ) : (
                            <NoResults
                                title={SystemText('Admin/UserGroup/NoResults/Title')}
                                message={SystemText('Admin/UserGroup/NoResults/Message')}
                            />
                        )}
                    </div>
                </Box>
            </section>

            <ProfileGroupModal
                key={mode === 'add' ? 'add' : profileGroup?.id}
                show={showProfilePicker}
                users={users}
                close={() => {
                    setShowProfilePicker(false);
                }}
                mode={mode}
                onSave={onSave}
                profileGroup={profileGroup}
                collections={collections}
            />
        </div>
    );
};

export default AdminProfileGroups;
